import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Star, MapPin, Instagram, ExternalLink, Lock, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function BarberProfile() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/barbers/${id}`).then(({ data }) => setB(data)).catch(() => setB(false)).finally(() => setLoading(false));
  }, [id]);

  const sendLead = async () => {
    if (!form.name || !form.contact || !form.message) { toast.error("Please fill in all fields."); return; }
    try {
      setSending(true);
      await api.post("/leads", { barber_id: id, ...form });
      toast.success("Message sent! The barber will reach out.");
      setForm({ name: "", contact: "", message: "" });
    } catch {
      toast.error("Could not send message.");
    } finally { setSending(false); }
  };

  if (loading) return <div className="py-32 flex justify-center"><div className="h-8 w-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!b) return <div className="py-32 text-center text-neutral-400">Barber not found. <Link to="/directory" className="text-[#D4AF37]">Back to directory</Link></div>;

  const canMessage = b.certified;

  return (
    <div>
      <section className="relative border-b border-white/10">
        <div className="h-64 md:h-80 overflow-hidden">
          <img src={b.portfolio?.[0] || b.avatar} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-black/20" />
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-24 relative pb-8">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <img src={b.avatar} alt={b.display_name} className="w-32 h-32 object-cover border-2 border-[#D4AF37] gold-glow" />
            <div className="flex-1 pt-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-3xl md:text-4xl font-bold">{b.display_name}</h1>
                {b.certified && <span className="flex items-center gap-1 bg-[#D4AF37] text-black text-xs font-mono px-2.5 py-1"><ShieldCheck size={13} /> Quality Certified</span>}
                {b.featured && <span className="flex items-center gap-1 border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-mono px-2.5 py-1"><Star size={13} /> Featured Pro</span>}
              </div>
              <p className="mt-2 text-neutral-300">{b.shop_name}</p>
              <div className="mt-3 flex flex-wrap items-center gap-5 text-sm text-neutral-400">
                <span className="flex items-center gap-1.5"><MapPin size={14} /> {b.city}{b.state ? `, ${b.state}` : ""}</span>
                <span className="flex items-center gap-1.5 text-[#D4AF37]"><Star size={14} fill="#D4AF37" /> {b.rating?.toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="font-display text-2xl font-bold">About</h2>
            <p className="mt-3 text-neutral-300 leading-relaxed">{b.bio || "This barber hasn't added a bio yet."}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {(b.specialties || []).map((s) => (
                <span key={s} className="text-sm px-3 py-1 border border-white/10 text-neutral-300">{s}</span>
              ))}
            </div>
          </div>

          {b.portfolio?.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold">Portfolio</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {b.portfolio.map((p, i) => (
                  <img key={`${p}-${i}`} src={p} alt={`work ${i + 1}`} className="w-full h-40 object-cover border border-white/10 hover:border-[#D4AF37]/50 transition-colors" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* contact / message */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="bg-[#111] border border-white/10 p-6">
            <h3 className="font-display text-lg font-semibold">Contact</h3>
            <div className="mt-4 space-y-3">
              {b.instagram && <a href={`https://instagram.com/${b.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-neutral-300 hover:text-[#D4AF37]"><Instagram size={16} /> @{b.instagram}</a>}
              {b.booking_url ? (
                <a href={b.booking_url} target="_blank" rel="noreferrer"><Button className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="barber-book-btn">Book Now <ExternalLink size={14} className="ml-2" /></Button></a>
              ) : (
                <p className="text-xs text-neutral-500">No public booking link.</p>
              )}
            </div>
          </div>

          <div className="bg-[#111] border border-white/10 p-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">Send a message {!canMessage && <Lock size={14} className="text-neutral-500" />}</h3>
            {canMessage ? (
              <div className="mt-4 space-y-3" data-testid="barber-message-form">
                <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0a0a0a] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" data-testid="lead-name" />
                <Input placeholder="Email or phone" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-[#0a0a0a] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" data-testid="lead-contact" />
                <Textarea placeholder="What do you need?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[#0a0a0a] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" data-testid="lead-message" />
                <Button onClick={sendLead} disabled={sending} className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="lead-submit">{sending ? "Sending…" : <>Send Message <Send size={14} className="ml-2" /></>}</Button>
              </div>
            ) : (
              <p className="mt-3 text-sm text-neutral-500">Private messaging is available for Quality Certified barbers. <Link to="/certification" className="text-[#D4AF37]">Learn more</Link></p>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
